import {
  Box,
  Chip,
  DropdownButton,
  FlexBox,
  Icon,
  Text,
} from '@dixa/lib-shared-habla';
import cx from 'classnames';
import type { SourceConversation } from 'lib-knowledge-remote-types/suggestions.types';

import s from './sourceConversationsChip.module.scss';
import { useSourceConversationsRenderer } from './sourceConversationsRenderer';

type SourceConversationsChipProps = {
  conversations: readonly SourceConversation[] | undefined;
  className?: string;
};

/**
 * A count chip that expands into the conversations an article or draft was based
 * on. Shared by the multi-conversation draft modal and the article editor header
 * so both surfaces present the same panel.
 *
 * Renders nothing when there are no conversations, or when the host app has not
 * injected a `renderSourceConversations` — the panel body is the app's, since
 * only it can resolve conversation display data.
 */
export function SourceConversationsChip({
  conversations,
  className,
}: SourceConversationsChipProps): JSX.Element | null {
  const renderSourceConversations = useSourceConversationsRenderer();

  if (!renderSourceConversations || !conversations?.length) return null;

  return (
    <DropdownButton
      noStyle
      fixed
      buttonProps={{ 'aria-label': 'Show source conversations' }}
      dropdown={
        <Box
          className={s.panel}
          width="480px"
          maxHeight="480px"
          padding="300 400"
          background="neutral.000"
          style={{ overflowY: 'auto' }}
        >
          <Text.Heading variant="heading-2">Conversations</Text.Heading>
          {renderSourceConversations(conversations)}
        </Box>
      }
    >
      <Chip noBorder className={cx(s.chip, className)}>
        <FlexBox alignItems="center" gap="100">
          <Icon name="chat" />
          {conversations.length}
        </FlexBox>
      </Chip>
    </DropdownButton>
  );
}
